import type { Table } from '@tanstack/react-table'
import { Eye, EyeOff, Search, Settings2, X } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface DataTableToolbarProps<TData> {
  table: Table<TData>
  searchKey?: string
  searchPlaceholder?: string
  enableColumnVisibility?: boolean
}

export function DataTableToolbar<TData>({
  table,
  searchKey,
  searchPlaceholder = 'Search...',
  enableColumnVisibility = true,
}: DataTableToolbarProps<TData>) {
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false)
  const isFiltered = table.getState().globalFilter?.length > 0
  const hiddenColumnsCount = table
    .getAllColumns()
    .filter((col) => col.getCanHide() && !col.getIsVisible()).length

  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex flex-1 items-center space-x-2">
        {searchKey !== undefined && (
          <div className="relative w-full max-w-sm">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder={searchPlaceholder}
              value={(table.getState().globalFilter as string) ?? ''}
              onChange={(event) => table.setGlobalFilter(event.target.value)}
              className="h-9 w-full pr-9 pl-9"
            />
            {isFiltered && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2"
                onClick={() => table.setGlobalFilter('')}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {enableColumnVisibility && (
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className="ml-auto h-9"
            onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Columns
            {hiddenColumnsCount > 0 && (
              <span className="bg-muted ml-2 rounded px-1.5 py-0.5 text-xs">
                {hiddenColumnsCount} hidden
              </span>
            )}
          </Button>

          {isColumnMenuOpen && (
            <>
              {/* Backdrop to close menu */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsColumnMenuOpen(false)}
              />
              <div className="bg-popover absolute top-full right-0 z-50 mt-2 w-56 rounded-md border p-2 shadow-md">
                <div className="mb-2 flex items-center justify-between px-2">
                  <span className="text-sm font-medium">Toggle columns</span>
                  {hiddenColumnsCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        table.getAllColumns().forEach((col) => {
                          if (col.getCanHide()) {
                            col.toggleVisibility(true)
                          }
                        })
                      }}
                    >
                      Show all
                    </Button>
                  )}
                </div>
                <div className="max-h-64 space-y-1 overflow-y-auto">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      const isVisible = column.getIsVisible()
                      return (
                        <button
                          key={column.id}
                          type="button"
                          className={cn(
                            'hover:bg-accent flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm',
                            !isVisible && 'text-muted-foreground',
                          )}
                          onClick={() => column.toggleVisibility(!isVisible)}
                        >
                          <span className="capitalize">
                            {column.id.replace(/_/g, ' ')}
                          </span>
                          {isVisible ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </button>
                      )
                    })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
